import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as SwapIcon } from '../../../assets/icons/arrange-circle-icon.svg';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setSelectedStationIds } from '../../../redux/slice/StationComparisonSlice';
import { fetchChargingStationList } from '../../../redux/slice/StationManagementSidePanelSlice';
import type { RootState } from '../../../redux/store';
import SelectField from '../../../shared-components/select-field/SelectField';
import ToggleWithIcons from '../../../shared-components/toggle-with-icons/ToggleWithIcons';
import stationDetailsApi from '../../../utils/api/station-management';

import StationCard from './StationCard';

/* -------------------------------------------------------------------------- */
/*                                   Types                                    */
/* -------------------------------------------------------------------------- */

export interface StationMetric {
  label: string;
  key: string;
}

export interface StationData {
  _id: string;
  name: string;
  location: string;
  totalRevenue: number;
  users: number;
  deviceCount: number;
  energyDispensed: string;
  connectors: number;
  uptime: string;
  chargingSessions: number;
  avgSessionDuration: string;
  healthScore: number;
  faultCount: number;
}

interface SelectOption {
  label: string;
  value: string;
}

/* -------------------------------------------------------------------------- */
/*                              Static Constants                              */
/* -------------------------------------------------------------------------- */

const MAX_COMPARE_COUNT = 4;

const METRICS: StationMetric[] = [
  { label: 'locationComparison', key: 'address' },
  { label: 'totalRevenueThisMonth', key: 'totalRevenue' },
  { label: 'users', key: 'consumerCount' },
  { label: 'deviceCount', key: 'deviceCount' },
  { label: 'energyDispensedKWh', key: 'electricityCostByUtilityProvider' },
  { label: 'connectors', key: 'totalConnectors' },
  { label: 'uptimeLast30Days', key: 'uptimeLast30Days' },
  { label: 'noOfChargingSessions', key: 'totalChargingSession' },
  { label: 'avgSessionDurationComparison', key: 'avgChargingHours' },
  { label: 'healthScoreComparison', key: 'healthScore' },
  { label: 'faultCountLast30Days', key: 'faultsCount' },
];

/* -------------------------------------------------------------------------- */
/*                              API Helper                                    */
/* -------------------------------------------------------------------------- */

/**
 * Fetch station details by ID
 * @param id Station ID
 * @returns StationData or null
 */
const fetchStation = async (id: string): Promise<StationData | null> => {
  try {
    const response = await stationDetailsApi.fetchStationDetails(id);
    return response.data as StationData;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Failed to fetch station ${id}`, error);
    return null;
  }
};

/* -------------------------------------------------------------------------- */
/*                              Component                                     */
/* -------------------------------------------------------------------------- */

/**
 * StationComparisonList
 * @returns {any} The Station Comparison List component.
 */
function StationComparisonList(): JSX.Element {
  const { t } = useTranslation('stationManagement');
  const dispatch = useAppDispatch();
  const chargingStationList = useAppSelector(
    (state: RootState) => state.stationManagement.chargingStationList,
  );

  const { selectedStationIds } = useAppSelector((state) => state.stationComparison);

  const [selectedDevices, setSelectedDevices] = useState<string[]>(
    selectedStationIds.length > 0 ? selectedStationIds : Array(MAX_COMPARE_COUNT).fill(''),
  );

  const [detailCache, setDetailCache] = useState<Record<string, StationData>>({});

  const [chartToggle, setChartToggle] = useState([
    { label: 'fullDetails', key: 'full', isActive: true },
    { label: 'differenceOnly', key: 'diff', isActive: false },
  ]);

  /* ------------------------------------------------------------------------ */
  /*                              Derived State                               */
  /* ------------------------------------------------------------------------ */

  const detailedStations = useMemo(
    () =>
      selectedDevices
        .map((id) => detailCache[id])
        .filter((station): station is StationData => Boolean(station)),
    [detailCache, selectedDevices],
  );

  const orderedDeviceOptions: SelectOption[] = useMemo(() => {
    if (!chargingStationList.length) {
      return [];
    }

    const selected = selectedDevices
      .filter(Boolean)
      .map((id) => chargingStationList.find((s) => s._id === id))
      .filter((station): station is { _id: string; name: string } => Boolean(station));

    const remaining = chargingStationList.filter(
      (station) => !selectedDevices.includes(station._id),
    );

    return [...selected, ...remaining].map((station) => ({
      label: station.name,
      value: station._id,
    }));
  }, [chargingStationList, selectedDevices]);

  /**
   * Number of visible selectors based on selection progress
   */
  const visibleSelectorCount = useMemo(() => {
    const firstEmptyIndex = selectedDevices.findIndex((id) => !id);

    // If none are empty, show all
    if (firstEmptyIndex === -1) {
      return MAX_COMPARE_COUNT;
    }

    // Always show at least one selector
    return Math.max(1, firstEmptyIndex + 1);
  }, [selectedDevices]);

  /* ------------------------------------------------------------------------ */
  /*                              Effects                                     */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    if (!chargingStationList.length) {
      dispatch(fetchChargingStationList());
    }
  }, [dispatch, chargingStationList.length]);

  useEffect(() => {
    const idsToFetch = selectedDevices.filter((id) => Boolean(id) && !detailCache[id]);
    if (!idsToFetch.length) return;
    dispatch(setSelectedStationIds(selectedDevices));

    /**
     * fetchAllStations
     */
    const fetchAllStations = async () => {
      const results = await Promise.all(idsToFetch.map(fetchStation));

      setDetailCache((previous) => {
        const updated = { ...previous };
        results.forEach((station) => {
          if (station) {
            updated[station._id] = station;
          }
        });
        return updated;
      });
    };

    fetchAllStations();
  }, [dispatch, detailCache, selectedDevices]);
  useEffect(() => {
    setSelectedDevices(
      selectedStationIds.length > 0 ? selectedStationIds : Array(MAX_COMPARE_COUNT).fill(''),
    );
  }, [selectedStationIds]);
  /* ------------------------------------------------------------------------ */
  /*                              Handlers                                    */
  /* ------------------------------------------------------------------------ */

  /**
   * Handle toggle click
   * @param key Toggle key
   */
  const handleClickToggle = useCallback((key: string) => {
    setChartToggle((previous) =>
      previous.map((tab) => ({
        ...tab,
        isActive: tab.key === key,
      })),
    );
  }, []);

  /**
   * Handle dropdown value change
   * @param index Column index
   * @param value Selected station ID
   */
  const handleChangeFunction = useCallback((index: number, value: string) => {
    setSelectedDevices((previous) => {
      const updated = [...previous];
      updated[index] = value;
      return updated;
    });
  }, []);

  /**
   * Swap two comparison columns
   * @param first First index
   * @param second Second index
   */
  const swapColumns = useCallback((first: number, second: number) => {
    setSelectedDevices((previous) => {
      const updated = [...previous];
      [updated[first], updated[second]] = [updated[second], updated[first]];
      return updated;
    });
  }, []);

  /**
   * Filter dropdown options to avoid duplicate selection
   * @param currentIndex Current dropdown index
   * @returns Filtered options
   */
  const getFilteredOptions = useCallback(
    (currentIndex: number): SelectOption[] =>
      orderedDeviceOptions.filter(
        (option) =>
          option.value === selectedDevices[currentIndex] || !selectedDevices.includes(option.value),
      ),
    [orderedDeviceOptions, selectedDevices],
  );

  /* ------------------------------------------------------------------------ */
  /*                              Render                                      */
  /* ------------------------------------------------------------------------ */

  return (
    <div className="station-comparison-list">
      <div className="selectors-list">
        <div className="station-comparison-toggle">
          <ToggleWithIcons
            leftMenu={{ ...chartToggle[0], label: t(chartToggle[0].label) }}
            rightMenu={{ ...chartToggle[1], label: t(chartToggle[1].label) }}
            handleClickToggle={handleClickToggle}
          />
        </div>

        {selectedDevices.slice(0, visibleSelectorCount).map((deviceId, index) => (
          <div key={`station-selector-${deviceId}`} className="selector-item">
            <div className="select-wrapper position">
              <SelectField
                options={getFilteredOptions(index)}
                placeholder={t('selectStation')}
                selectedValue={deviceId}
                name={`selectedDevice${index}`}
                onChangeValue={(_, value) => handleChangeFunction(index, value)}
              />
            </div>

            {index < visibleSelectorCount - 1 && (
              <div className="swap-button">
                <SwapIcon className="swap-icon" onClick={() => swapColumns(index, index + 1)} />
              </div>
            )}
          </div>
        ))}
      </div>

      <StationCard
        stations={detailedStations}
        metrics={METRICS.map((m) => ({ ...m, label: t(m.label) }))}
        heading={t('stationComparison')}
        differenceOnly={chartToggle[1].isActive}
      />
    </div>
  );
}

export default StationComparisonList;
